import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { getSelectOptions } from '@anytag/shared-utils';
import {
  useSearchPrivateTalentInfluencersLazyQuery,
  useSearchPrivatePartnerInfluencersLazyQuery
} from '@anytag/graphql';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  PrivateInfluencerSearchOption,
  AsyncSearchPrivateInfluencersFieldProps,
  SingleAsyncSearchPrivateInfluencersFieldProps,
  MultipleAsyncSearchPrivateInfluencersFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchPrivateInfluencersField<T extends string>(
  props: MultipleAsyncSearchPrivateInfluencersFieldProps<T>
): React.ReactElement;
export function AsyncSearchPrivateInfluencersField<T extends string>(
  props: SingleAsyncSearchPrivateInfluencersFieldProps<T>
): React.ReactElement;
export function AsyncSearchPrivateInfluencersField<T extends string>({
  name,
  help,
  title,
  multiple,
  agencyId,
  agencyType,
  searchProps,
  ...restProps
}: AsyncSearchPrivateInfluencersFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getSearchPrivateTalentInfluencers, loading: fetchingTalent } = useSearchPrivateTalentInfluencersLazyQuery();
  const { getSearchPrivatePartnerInfluencers, loading: fetchingPartner } =
    useSearchPrivatePartnerInfluencersLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<PrivateInfluencerSearchOption> }>();

  const value = watch(name);
  const selectedInfluencers = getValuesArray({ value, multiple }) as string[];

  const fetchPrivateInfluencers: AsyncSelectorSearchFieldProps<T, PrivateInfluencerSearchOption>['onSearch'] = async (
    keyword
  ) => {
    let influencers: PrivateInfluencerSearchOption[] = [];
    const alreadySelectedInfluencers = selectedInfluencers.map(Number);

    try {
      if (agencyType === 'talent') {
        // TODO: BE is return wrong data for alreadySelectedInfluencers filter, we need to fix it
        const { data } = await getSearchPrivateTalentInfluencers({
          variables: {
            keyword: keyword || '',
            talentAgencyId: agencyId,
            alreadySelectedInfluencers
          }
        });
        influencers = getSelectOptions(data?.searchPrivateTalentInfluencers || []);
      } else if (agencyType === 'partner') {
        // TODO: BE is return wrong data for alreadySelectedInfluencers filter, we need to fix it
        const { data } = await getSearchPrivatePartnerInfluencers({
          variables: {
            keyword: keyword || '',
            partnerId: agencyId,
            alreadySelectedInfluencers
          }
        });
        influencers = getSelectOptions(data?.searchPrivatePartnerInfluencers || []);
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return influencers;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, PrivateInfluencerSearchOption>,
    'name' | 'loading' | 'onSearch' | 'title' | 'help' | 'searchProps'
  > = {
    name,
    onSearch: fetchPrivateInfluencers,
    loading: fetchingTalent || fetchingPartner,
    help: help ?? t<string>('Tooltip.Private Influencers'),
    title: title ?? t<string>('Selector.Private influencers'),
    searchProps: { placeholder: t<string>('Search by influencer name'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, PrivateInfluencerSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchPrivateInfluencersFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, PrivateInfluencerSearchOption>
      {...(restProps as Partial<SingleAsyncSearchPrivateInfluencersFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
